import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Leaf, Shield, Droplets, Trash2, Recycle, Plus, Calculator,
  ChevronDown, ChevronUp, AlertTriangle, TreePine, Settings, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { toast } from 'sonner';
import {
  erosionControlDatabase,
  stormwaterDatabase,
  remediationDatabase,
  greenBuildingDatabase
} from '../data/environmentalMaterials';

const formatCurrency = (num) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num);
};

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const EnvironmentalCalculator = ({ environmentalItems, setEnvironmentalItems }) => {
  const [activeTab, setActiveTab] = useState('erosion');
  const [isOpen, setIsOpen] = useState(true);
  const [showCustomManager, setShowCustomManager] = useState(false);

  // Custom Materials State
  const [customErosionItems, setCustomErosionItems] = useState(() => {
    const saved = localStorage.getItem('customErosionItems');
    return saved ? JSON.parse(saved) : [];
  });
  const [customStormwaterItems, setCustomStormwaterItems] = useState(() => {
    const saved = localStorage.getItem('customStormwaterItems');
    return saved ? JSON.parse(saved) : [];
  });
  const [customRemediationItems, setCustomRemediationItems] = useState(() => {
    const saved = localStorage.getItem('customRemediationItems');
    return saved ? JSON.parse(saved) : [];
  });
  const [customGreenItems, setCustomGreenItems] = useState(() => {
    const saved = localStorage.getItem('customGreenItems');
    return saved ? JSON.parse(saved) : [];
  });

  // Custom item form state
  const [newCustomItem, setNewCustomItem] = useState({
    name: '', category: 'Custom', costPerUnit: '', unit: 'LF', description: ''
  });

  // Input state for each tab
  const [erosionItem, setErosionItem] = useState('');
  const [erosionQuantity, setErosionQuantity] = useState('');
  const [erosionCost, setErosionCost] = useState('');
  
  const [stormwaterItem, setStormwaterItem] = useState('');
  const [stormwaterQuantity, setStormwaterQuantity] = useState('');
  const [stormwaterCost, setStormwaterCost] = useState('');
  
  const [remediationItem, setRemediationItem] = useState('');
  const [remediationQuantity, setRemediationQuantity] = useState('');
  const [remediationCost, setRemediationCost] = useState('');
  
  const [greenItem, setGreenItem] = useState('');
  const [greenQuantity, setGreenQuantity] = useState('');
  const [greenCost, setGreenCost] = useState('');

  // Save custom materials to localStorage
  useEffect(() => {
    localStorage.setItem('customErosionItems', JSON.stringify(customErosionItems));
  }, [customErosionItems]);
  useEffect(() => {
    localStorage.setItem('customStormwaterItems', JSON.stringify(customStormwaterItems));
  }, [customStormwaterItems]);
  useEffect(() => {
    localStorage.setItem('customRemediationItems', JSON.stringify(customRemediationItems));
  }, [customRemediationItems]);
  useEffect(() => {
    localStorage.setItem('customGreenItems', JSON.stringify(customGreenItems));
  }, [customGreenItems]);

  // Combined materials lists
  const allErosionItems = useMemo(() => [...erosionControlDatabase, ...customErosionItems], [customErosionItems]);
  const allStormwaterItems = useMemo(() => [...stormwaterDatabase, ...customStormwaterItems], [customStormwaterItems]);
  const allRemediationItems = useMemo(() => [...remediationDatabase, ...customRemediationItems], [customRemediationItems]);
  const allGreenItems = useMemo(() => [...greenBuildingDatabase, ...customGreenItems], [customGreenItems]);

  const generateId = useCallback(() => {
    return `env_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Add custom item functions
  const addCustomErosionItem = () => {
    if (!newCustomItem.name || !newCustomItem.costPerUnit) {
      toast.error('Please enter name and cost');
      return;
    }
    const newSpec = {
      id: generateId(),
      name: newCustomItem.name,
      category: 'Custom',
      costPerLinearFt: newCustomItem.unit === 'LF' ? parseFloat(newCustomItem.costPerUnit) : 0,
      costPerSquareFt: newCustomItem.unit === 'SF' ? parseFloat(newCustomItem.costPerUnit) : 0,
      costPerEach: newCustomItem.unit === 'EA' ? parseFloat(newCustomItem.costPerUnit) : 0,
      description: newCustomItem.description || 'Custom erosion control item',
      isCustom: true
    };
    setCustomErosionItems([...customErosionItems, newSpec]);
    toast.success('Custom erosion item added');
    setNewCustomItem({ name: '', category: 'Custom', costPerUnit: '', unit: 'LF', description: '' });
  };

  const addCustomStormwaterItem = () => {
    if (!newCustomItem.name || !newCustomItem.costPerUnit) {
      toast.error('Please enter name and cost');
      return;
    }
    const newSpec = {
      id: generateId(),
      name: newCustomItem.name,
      category: 'Custom',
      costPerCubicYard: newCustomItem.unit === 'CY' ? parseFloat(newCustomItem.costPerUnit) : 0,
      costPerLinearFt: newCustomItem.unit === 'LF' ? parseFloat(newCustomItem.costPerUnit) : 0,
      costPerSquareFt: newCustomItem.unit === 'SF' ? parseFloat(newCustomItem.costPerUnit) : 0,
      costPerEach: newCustomItem.unit === 'EA' ? parseFloat(newCustomItem.costPerUnit) : 0,
      description: newCustomItem.description || 'Custom stormwater item',
      isCustom: true
    };
    setCustomStormwaterItems([...customStormwaterItems, newSpec]);
    toast.success('Custom stormwater item added');
    setNewCustomItem({ name: '', category: 'Custom', costPerUnit: '', unit: 'CY', description: '' });
  };

  const addCustomRemediationItem = () => {
    if (!newCustomItem.name || !newCustomItem.costPerUnit) {
      toast.error('Please enter name and cost');
      return;
    }
    const newSpec = {
      id: generateId(),
      name: newCustomItem.name,
      category: 'Custom',
      costPerCubicYard: newCustomItem.unit === 'CY' ? parseFloat(newCustomItem.costPerUnit) : 0,
      costPerTon: newCustomItem.unit === 'TON' ? parseFloat(newCustomItem.costPerUnit) : 0,
      costPerSquareFt: newCustomItem.unit === 'SF' ? parseFloat(newCustomItem.costPerUnit) : 0,
      description: newCustomItem.description || 'Custom remediation item',
      isCustom: true
    };
    setCustomRemediationItems([...customRemediationItems, newSpec]);
    toast.success('Custom remediation item added');
    setNewCustomItem({ name: '', category: 'Custom', costPerUnit: '', unit: 'CY', description: '' });
  };

  const addCustomGreenItem = () => {
    if (!newCustomItem.name || !newCustomItem.costPerUnit) {
      toast.error('Please enter name and cost');
      return;
    }
    const newSpec = {
      id: generateId(),
      name: newCustomItem.name,
      category: 'Custom',
      costPerTon: newCustomItem.unit === 'TON' ? parseFloat(newCustomItem.costPerUnit) : 0,
      costPerSquareFt: newCustomItem.unit === 'SF' ? parseFloat(newCustomItem.costPerUnit) : 0,
      costPerCubicYard: newCustomItem.unit === 'CY' ? parseFloat(newCustomItem.costPerUnit) : 0,
      description: newCustomItem.description || 'Custom green building item',
      isCustom: true
    };
    setCustomGreenItems([...customGreenItems, newSpec]);
    toast.success('Custom green item added');
    setNewCustomItem({ name: '', category: 'Custom', costPerUnit: '', unit: 'TON', description: '' });
  };

  // Delete custom item
  const deleteCustomItem = (type, id) => {
    if (type === 'erosion') {
      setCustomErosionItems(customErosionItems.filter(i => i.id !== id));
    } else if (type === 'stormwater') {
      setCustomStormwaterItems(customStormwaterItems.filter(i => i.id !== id));
    } else if (type === 'remediation') {
      setCustomRemediationItems(customRemediationItems.filter(i => i.id !== id));
    } else if (type === 'green') {
      setCustomGreenItems(customGreenItems.filter(i => i.id !== id));
    }
    toast.error('Custom item removed');
  };

  // Get unit for an item (without pre-populated cost)
  const getItemUnit = (item) => {
    if (item.costPerLinearFt) return 'LF';
    if (item.costPerSquareFt) return 'SF';
    if (item.costPerSquareYard) return 'SY';
    if (item.costPerCubicYard) return 'CY';
    if (item.costPerEach) return 'EA';
    if (item.costPerTon) return 'TON';
    if (item.costPerGallon) return 'GAL';
    if (item.costPerThousandGallons) return 'KGAL';
    return 'EA';
  };

  // Add Erosion Control Item
  const addErosionItem = () => {
    if (!erosionItem || !erosionQuantity || !erosionCost) {
      toast.error('Please select item, enter quantity and cost');
      return;
    }

    const item = allErosionItems.find(e => e.name === erosionItem);
    if (!item) return;

    const qty = parseFloat(erosionQuantity);
    const unitCost = parseFloat(erosionCost);
    const totalCost = unitCost * qty;
    const unit = getItemUnit(item);

    const newItem = {
      id: generateId(),
      type: 'erosion',
      name: item.name,
      category: item.category,
      quantity: qty,
      unit: unit,
      unitCost: unitCost,
      totalCost,
      lifespan: item.lifespan || null,
      isCustom: item.isCustom || false,
      addedAt: new Date().toISOString()
    };

    setEnvironmentalItems([...environmentalItems, newItem]);
    toast.success(`${item.name} added`);
    setErosionItem('');
    setErosionQuantity('');
    setErosionCost('');
  };

  // Add Stormwater Item
  const addStormwaterItem = () => {
    if (!stormwaterItem || !stormwaterQuantity || !stormwaterCost) {
      toast.error('Please select item, enter quantity and cost');
      return;
    }

    const item = allStormwaterItems.find(s => s.name === stormwaterItem);
    if (!item) return;

    const qty = parseFloat(stormwaterQuantity);
    const unitCost = parseFloat(stormwaterCost);
    const totalCost = unitCost * qty;
    const unit = getItemUnit(item);

    const newItem = {
      id: generateId(),
      type: 'stormwater',
      name: item.name,
      category: item.category,
      quantity: qty,
      unit: unit,
      unitCost: unitCost,
      totalCost,
      storagePerFt: item.storagePerFt || null,
      isCustom: item.isCustom || false,
      totalStorage: item.storagePerFt ? qty * item.storagePerFt : null,
      addedAt: new Date().toISOString()
    };

    setEnvironmentalItems([...environmentalItems, newItem]);
    toast.success(`${item.name} added`);
    setStormwaterItem('');
    setStormwaterQuantity('');
    setStormwaterCost('');
  };

  // Add Remediation Item
  const addRemediationItem = () => {
    if (!remediationItem || !remediationQuantity || !remediationCost) {
      toast.error('Please select item, enter quantity and cost');
      return;
    }

    const item = allRemediationItems.find(r => r.name === remediationItem);
    if (!item) return;

    const qty = parseFloat(remediationQuantity);
    const unitCost = parseFloat(remediationCost);
    const totalCost = unitCost * qty;
    const unit = getItemUnit(item);

    const newItem = {
      id: generateId(),
      type: 'remediation',
      name: item.name,
      category: item.category,
      quantity: qty,
      unit: unit,
      unitCost: unitCost,
      totalCost,
      isHazardous: item.category === 'Hazmat Abatement' || item.name.includes('Hazardous'),
      isCustom: item.isCustom || false,
      addedAt: new Date().toISOString()
    };

    setEnvironmentalItems([...environmentalItems, newItem]);
    toast.success(`${item.name} added`);
    setRemediationItem('');
    setRemediationQuantity('');
    setRemediationCost('');
  };

  // Add Green Building Item
  const addGreenItem = () => {
    if (!greenItem || !greenQuantity || !greenCost) {
      toast.error('Please select item, enter quantity and cost');
      return;
    }

    const item = allGreenItems.find(g => g.name === greenItem);
    if (!item) return;

    const qty = parseFloat(greenQuantity);
    const unitCost = parseFloat(greenCost);
    const totalCost = unitCost * qty;
    const unit = getItemUnit(item);

    // Calculate carbon impact
    let carbonImpact = 0;
    if (item.carbonSavings) {
      carbonImpact = -item.carbonSavings * qty; // Negative = savings
    } else if (item.carbonPerTon) {
      carbonImpact = item.carbonPerTon * qty; // Positive = emissions
    } else if (item.carbonSequestration) {
      carbonImpact = -item.carbonSequestration * qty; // Negative = sequestration
    }

    const newItem = {
      id: generateId(),
      type: 'green',
      name: item.name,
      category: item.category,
      quantity: qty,
      unit,
      unitCost,
      totalCost,
      recycledContent: item.recycledContent || 0,
      carbonImpact,
      isCustom: item.isCustom || false,
      addedAt: new Date().toISOString()
    };

    setEnvironmentalItems([...environmentalItems, newItem]);
    toast.success(`${item.name} added`);
    setGreenItem('');
    setGreenQuantity('');
    setGreenCost('');
  };

  // Delete item
  const deleteItem = (id) => {
    setEnvironmentalItems(environmentalItems.filter(item => item.id !== id));
    toast.error('Item removed');
  };

  // Calculate totals
  const totals = environmentalItems.reduce((acc, item) => {
    acc.totalCost += item.totalCost || 0;
    acc.itemCount++;
    if (item.carbonImpact) acc.carbonImpact += item.carbonImpact;
    if (item.totalStorage) acc.totalStorage += item.totalStorage;
    return acc;
  }, { totalCost: 0, itemCount: 0, carbonImpact: 0, totalStorage: 0 });

  // Group items by category
  const groupByCategory = (database) => {
    return database.reduce((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    }, {});
  };

  const erosionGroups = groupByCategory(allErosionItems);
  const stormwaterGroups = groupByCategory(allStormwaterItems);
  const remediationGroups = groupByCategory(allRemediationItems);
  const greenGroups = groupByCategory(allGreenItems);

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Items</span>
              <Leaf className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold">{totals.itemCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Cost</span>
              <Calculator className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold">{formatCurrency(totals.totalCost)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Carbon Impact</span>
              <TreePine className="w-4 h-4 text-emerald-500" />
            </div>
            <p className={`text-2xl font-bold ${totals.carbonImpact <= 0 ? 'text-green-600' : 'text-orange-600'}`}>
              {totals.carbonImpact <= 0 ? '-' : '+'}{Math.abs(totals.carbonImpact).toFixed(1)} t CO₂
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Storage</span>
              <Droplets className="w-4 h-4 text-cyan-500" />
            </div>
            <p className="text-2xl font-bold">{formatNumber(totals.totalStorage, 0)} CF</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Calculator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-green-600" />
              Environmental Calculator
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p className="text-sm">Calculate costs for erosion control, stormwater management, environmental remediation, and green building materials. Select from predefined options or add custom items.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Dialog open={showCustomManager} onOpenChange={setShowCustomManager}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Custom
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Manage Custom Environmental Materials</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="erosion" className="mt-4">
                  <TabsList className="grid grid-cols-4 mb-4">
                    <TabsTrigger value="erosion">Erosion</TabsTrigger>
                    <TabsTrigger value="stormwater">Stormwater</TabsTrigger>
                    <TabsTrigger value="remediation">Remediation</TabsTrigger>
                    <TabsTrigger value="green">Green</TabsTrigger>
                  </TabsList>

                  {/* Custom Erosion */}
                  <TabsContent value="erosion" className="space-y-4">
                    <div className="grid grid-cols-5 gap-2">
                      <Input placeholder="Name" value={newCustomItem.name} onChange={(e) => setNewCustomItem({...newCustomItem, name: e.target.value})} />
                      <Input type="number" placeholder="Cost" value={newCustomItem.costPerUnit} onChange={(e) => setNewCustomItem({...newCustomItem, costPerUnit: e.target.value})} />
                      <Select value={newCustomItem.unit} onValueChange={(v) => setNewCustomItem({...newCustomItem, unit: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LF">Per LF</SelectItem>
                          <SelectItem value="SF">Per SF</SelectItem>
                          <SelectItem value="EA">Each</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder="Description" value={newCustomItem.description} onChange={(e) => setNewCustomItem({...newCustomItem, description: e.target.value})} />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button type="button" onClick={addCustomErosionItem}><Plus className="w-4 h-4 mr-1" />Add</Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-sm">Add your own custom erosion control material with your pricing</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {customErosionItems.length > 0 && (
                      <div className="space-y-2">
                        {customErosionItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                            <span>{item.name} - {formatCurrency(item.costPerLinearFt || item.costPerSquareYard || item.costPerEach)}</span>
                            <Button variant="ghost" size="sm" onClick={() => deleteCustomItem('erosion', item.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Custom Stormwater */}
                  <TabsContent value="stormwater" className="space-y-4">
                    <div className="grid grid-cols-5 gap-2">
                      <Input placeholder="Name" value={newCustomItem.name} onChange={(e) => setNewCustomItem({...newCustomItem, name: e.target.value})} />
                      <Input type="number" placeholder="Cost" value={newCustomItem.costPerUnit} onChange={(e) => setNewCustomItem({...newCustomItem, costPerUnit: e.target.value})} />
                      <Select value={newCustomItem.unit} onValueChange={(v) => setNewCustomItem({...newCustomItem, unit: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CY">Per CY</SelectItem>
                          <SelectItem value="LF">Per LF</SelectItem>
                          <SelectItem value="SF">Per SF</SelectItem>
                          <SelectItem value="EA">Each</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder="Description" value={newCustomItem.description} onChange={(e) => setNewCustomItem({...newCustomItem, description: e.target.value})} />
                      <Button type="button" onClick={addCustomStormwaterItem}><Plus className="w-4 h-4 mr-1" />Add</Button>
                    </div>
                    {customStormwaterItems.length > 0 && (
                      <div className="space-y-2">
                        {customStormwaterItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                            <span>{item.name}</span>
                            <Button variant="ghost" size="sm" onClick={() => deleteCustomItem('stormwater', item.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Custom Remediation */}
                  <TabsContent value="remediation" className="space-y-4">
                    <div className="grid grid-cols-5 gap-2">
                      <Input placeholder="Name" value={newCustomItem.name} onChange={(e) => setNewCustomItem({...newCustomItem, name: e.target.value})} />
                      <Input type="number" placeholder="Cost" value={newCustomItem.costPerUnit} onChange={(e) => setNewCustomItem({...newCustomItem, costPerUnit: e.target.value})} />
                      <Select value={newCustomItem.unit} onValueChange={(v) => setNewCustomItem({...newCustomItem, unit: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CY">Per CY</SelectItem>
                          <SelectItem value="TON">Per Ton</SelectItem>
                          <SelectItem value="SF">Per SF</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder="Description" value={newCustomItem.description} onChange={(e) => setNewCustomItem({...newCustomItem, description: e.target.value})} />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button type="button" onClick={addCustomRemediationItem}><Plus className="w-4 h-4 mr-1" />Add</Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-sm">Add custom remediation item like soil treatment or contamination cleanup</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {customRemediationItems.length > 0 && (
                      <div className="space-y-2">
                        {customRemediationItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                            <span>{item.name}</span>
                            <Button variant="ghost" size="sm" onClick={() => deleteCustomItem('remediation', item.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Custom Green */}
                  <TabsContent value="green" className="space-y-4">
                    <div className="grid grid-cols-5 gap-2">
                      <Input placeholder="Name" value={newCustomItem.name} onChange={(e) => setNewCustomItem({...newCustomItem, name: e.target.value})} />
                      <Input type="number" placeholder="Cost" value={newCustomItem.costPerUnit} onChange={(e) => setNewCustomItem({...newCustomItem, costPerUnit: e.target.value})} />
                      <Select value={newCustomItem.unit} onValueChange={(v) => setNewCustomItem({...newCustomItem, unit: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="TON">Per Ton</SelectItem>
                          <SelectItem value="SF">Per SF</SelectItem>
                          <SelectItem value="CY">Per CY</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder="Description" value={newCustomItem.description} onChange={(e) => setNewCustomItem({...newCustomItem, description: e.target.value})} />
                      <Button type="button" onClick={addCustomGreenItem} className="bg-green-600 hover:bg-green-700"><Plus className="w-4 h-4 mr-1" />Add</Button>
                    </div>
                    {customGreenItems.length > 0 && (
                      <div className="space-y-2">
                        {customGreenItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                            <span>{item.name}</span>
                            <Button variant="ghost" size="sm" onClick={() => deleteCustomItem('green', item.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>
          </CardTitle>
          <CardDescription>
            Erosion control, stormwater management, remediation, and green building
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 mb-6">
              <TabsTrigger value="erosion" className="text-xs">
                <Shield className="w-3 h-3 mr-1" />Erosion
              </TabsTrigger>
              <TabsTrigger value="stormwater" className="text-xs">
                <Droplets className="w-3 h-3 mr-1" />Stormwater
              </TabsTrigger>
              <TabsTrigger value="remediation" className="text-xs">
                <AlertTriangle className="w-3 h-3 mr-1" />Remediation
              </TabsTrigger>
              <TabsTrigger value="green" className="text-xs">
                <Recycle className="w-3 h-3 mr-1" />Green/LEED
              </TabsTrigger>
            </TabsList>

            {/* EROSION CONTROL TAB */}
            <TabsContent value="erosion" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Erosion Control Item</Label>
                  <Select value={erosionItem} onValueChange={setErosionItem}>
                    <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {Object.entries(erosionGroups).map(([category, items]) => (
                        <React.Fragment key={category}>
                          <div className="px-2 py-1 text-xs font-semibold bg-muted">{category}</div>
                          {items.map(item => (
                            <SelectItem key={item.name} value={item.name}>
                              {item.isCustom && '⭐ '}{item.name}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity (LF, SF, or Each)</Label>
                  <Input
                    type="number"
                    placeholder="Enter quantity"
                    value={erosionQuantity}
                    onChange={(e) => setErosionQuantity(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cost ($/unit)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter cost"
                    value={erosionCost}
                    onChange={(e) => setErosionCost(e.target.value)}
                  />
                </div>
              </div>
              <Button onClick={addErosionItem} className="w-full">
                <Plus className="w-4 h-4 mr-2" />Add Erosion Control
              </Button>
            </TabsContent>

            {/* STORMWATER TAB */}
            <TabsContent value="stormwater" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Stormwater Item</Label>
                  <Select value={stormwaterItem} onValueChange={setStormwaterItem}>
                    <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {Object.entries(stormwaterGroups).map(([category, items]) => (
                        <React.Fragment key={category}>
                          <div className="px-2 py-1 text-xs font-semibold bg-muted">{category}</div>
                          {items.map(item => (
                            <SelectItem key={item.name} value={item.name}>
                              {item.isCustom && '⭐ '}{item.name}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    placeholder="Enter quantity"
                    value={stormwaterQuantity}
                    onChange={(e) => setStormwaterQuantity(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cost ($/unit)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter cost"
                    value={stormwaterCost}
                    onChange={(e) => setStormwaterCost(e.target.value)}
                  />
                </div>
              </div>
              <Button onClick={addStormwaterItem} className="w-full">
                <Plus className="w-4 h-4 mr-2" />Add Stormwater Item
              </Button>
            </TabsContent>

            {/* REMEDIATION TAB */}
            <TabsContent value="remediation" className="space-y-4">
              <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg mb-4">
                <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">Environmental remediation may require permits and specialized contractors</span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Remediation Item</Label>
                  <Select value={remediationItem} onValueChange={setRemediationItem}>
                    <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {Object.entries(remediationGroups).map(([category, items]) => (
                        <React.Fragment key={category}>
                          <div className="px-2 py-1 text-xs font-semibold bg-muted">{category}</div>
                          {items.map(item => (
                            <SelectItem key={item.name} value={item.name}>
                              {item.isCustom && '⭐ '}{item.name}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    placeholder="Enter quantity"
                    value={remediationQuantity}
                    onChange={(e) => setRemediationQuantity(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cost ($/unit)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter cost"
                    value={remediationCost}
                    onChange={(e) => setRemediationCost(e.target.value)}
                  />
                </div>
              </div>
              <Button onClick={addRemediationItem} className="w-full">
                <Plus className="w-4 h-4 mr-2" />Add Remediation Item
              </Button>
            </TabsContent>

            {/* GREEN BUILDING TAB */}
            <TabsContent value="green" className="space-y-4">
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <Recycle className="w-4 h-4" />
                  <span className="text-sm">Track recycled content and carbon footprint for LEED credits</span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Green Building Item</Label>
                  <Select value={greenItem} onValueChange={setGreenItem}>
                    <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {Object.entries(greenGroups).map(([category, items]) => (
                        <React.Fragment key={category}>
                          <div className="px-2 py-1 text-xs font-semibold bg-muted">{category}</div>
                          {items.map(item => (
                            <SelectItem key={item.name} value={item.name}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity (tons, SF, or CY)</Label>
                  <Input
                    type="number"
                    placeholder="Enter quantity"
                    value={greenQuantity}
                    onChange={(e) => setGreenQuantity(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cost ($/unit)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter cost"
                    value={greenCost}
                    onChange={(e) => setGreenCost(e.target.value)}
                  />
                </div>
              </div>
              <Button onClick={addGreenItem} className="w-full bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />Add Green Building Item
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Items List */}
      {environmentalItems.length > 0 && (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-green-600" />
                    Environmental Items ({environmentalItems.length})
                  </span>
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="space-y-3">
                  {environmentalItems.map(item => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.name}</span>
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              item.type === 'erosion' ? 'border-orange-500 text-orange-600' :
                              item.type === 'stormwater' ? 'border-blue-500 text-blue-600' :
                              item.type === 'remediation' ? 'border-red-500 text-red-600' :
                              'border-green-500 text-green-600'
                            }`}
                          >
                            {item.type}
                          </Badge>
                          {item.isHazardous && (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />HAZMAT
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          <span>{formatNumber(item.quantity, 0)} {item.unit} • </span>
                          {item.carbonImpact && (
                            <span className={item.carbonImpact <= 0 ? 'text-green-600' : 'text-orange-600'}>
                              {item.carbonImpact <= 0 ? '-' : '+'}{Math.abs(item.carbonImpact).toFixed(2)} t CO₂ • 
                            </span>
                          )}
                          <span className="text-green-600 font-medium">{formatCurrency(item.totalCost)}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteItem(item.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Summary */}
                <div className="mt-6 p-4 bg-green-500/10 rounded-lg">
                  <h4 className="font-semibold mb-3 text-green-700 dark:text-green-400">
                    Environmental Summary
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Total Items:</span>
                      <span className="font-bold ml-2">{totals.itemCount}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total Cost:</span>
                      <span className="font-bold ml-2 text-green-600">{formatCurrency(totals.totalCost)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Carbon Impact:</span>
                      <span className={`font-bold ml-2 ${totals.carbonImpact <= 0 ? 'text-green-600' : 'text-orange-600'}`}>
                        {totals.carbonImpact <= 0 ? 'Saves' : 'Emits'} {Math.abs(totals.carbonImpact).toFixed(2)} t CO₂
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">SW Storage:</span>
                      <span className="font-bold ml-2">{formatNumber(totals.totalStorage, 0)} CF</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}
    </div>
  );
};

export default EnvironmentalCalculator;
